import { initialCollaborationText } from '../../../lib/composer/collaborationMode.js';
// ChatWorkspace — raiz da nova superfície Chat (fatias A0+A1).
//
// Super-evolução da aba Chat num workspace de IA unificado: composer CENTRAL
// (home) + sidebar Agentes/Sessões+Pastas, sem nenhum chrome de github, na
// paleta warm do Cursor (.cx-shell). Reusa a MESMA máquina de runtime do Code:
// `createCodeSession` (scope='local') auto-ativa a sessão → `SessionWorkspace`
// renderiza a conversa (ConversationFeed) e roda o agent loop real via o path
// vivo /agent-turn. "Mexer no computador" = esse agent loop (fatia A2 liga as
// tools/skills/@mentions; aqui é o esqueleto funcional).

import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import './chat-workspace.css';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { useAuthStore } from '../../../store/useAuthStore.js';
import ChatWorkspaceSidebar from './ChatWorkspaceSidebar.jsx';
import ChatHomeComposer from './ChatHomeComposer.jsx';
import ApoloEmblem from './ApoloEmblem.jsx';
import { serializeAttachmentsWithContent } from '../../../hooks/useFileAttachments.js';
import SessionWorkspace from '../../code/SessionWorkspace.jsx';
import ChatTasksRail from './ChatTasksRail.jsx';
import MobileDrawer from '../../MobileDrawer.jsx';
import { buildNewChatIntent, intentToSessionInput } from '../../../lib/composer/newChatIntent.js';
// Agenda/Kanban da linha de nav — as MESMAS views da aba Code (D1-backed,
// conteúdo compartilhado). Lazy: só montam quando o Pedro abre a view, sem
// pesar o boot do Chat (mesmo padrão do CodeView).
const SchedulesView = lazy(() => import('../../schedules/SchedulesView.jsx'));
const KanbanBoard = lazy(() => import('../../kanban/KanbanBoard.jsx'));

// Plan e Deep research não fingem geração — transformam a 1ª mensagem pra usar
// as capacidades REAIS do agente (planejar antes de executar; pesquisar a fundo
// com verificação + citações). chat/image/video passam direto.


function greetingFor(name) {
  const h = new Date().getHours();
  const part = h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
  const first = (name || '').trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

export default function ChatWorkspace() {
  const draftIdentity = useAuthStore((state) => `${state.user?.id || 'anonymous'}:${state.activeProfileId || 'default'}`);
  const refreshSessions = useAgentStore((s) => s.refreshSessions);
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const sessions = useAgentStore((s) => s.code.sessions);
  const setActiveSession = useAgentStore((s) => s.setActiveSession);
  const user = useAuthStore((s) => s.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // View da aba Chat: 'home' (composer) | 'schedules' (Agenda) | 'sprints'
  // (Kanban). Setada pela linha de nav da sidebar via `apolo:setChatView`
  // (evento PRÓPRIO da aba Chat, distinto do usado pela aba Code — pra não vazar
  // entre abas, que ficam montadas com display:none no keep-alive do AppShell).
  const [chatView, setChatView] = useState('home');
  const shellRef = useRef(null);

  const activeSession = sessions.find((s) => s.id === activeSessionId) || null;

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  // Troca de view pela nav: abrir Agenda/Kanban fecha a sessão ativa (mesma
  // semântica do CodeView) pra a view ocupar o main.
  useEffect(() => {
    function onSetChatView(e) {
      const v = e?.detail?.view;
      if (v === 'home' || v === 'schedules' || v === 'sprints') {
        setChatView(v);
        if (useAgentStore.getState().code.activeSessionId) {
          useAgentStore.getState().setActiveSession(null);
        }
      }
    }
    window.addEventListener('apolo:setChatView', onSetChatView);
    return () => window.removeEventListener('apolo:setChatView', onSetChatView);
  }, []);

  // ⌘N / Ctrl+N → nova sessão (mesma ação do botão "Nova sessão": volta pra home
  // + limpa/foca o composer). Guard de visibilidade: as abas ficam montadas com
  // display:none no keep-alive do AppShell, então só disparamos quando o shell do
  // Chat está de fato visível (offsetParent === null ⇒ display:none em algum
  // ancestral) — senão o atalho vazaria pra Code/outras views. preventDefault
  // segura o "nova janela" nativo do browser/Electron.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key?.toLowerCase() !== 'n' || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      if (!shellRef.current || shellRef.current.offsetParent === null) return;
      e.preventDefault();
      setActiveSession(null);
      window.dispatchEvent(new CustomEvent('apolo:chatNewSession'));
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setActiveSession]);

  // Hamburger global (AppShell) abre o drawer mobile.
  useEffect(() => {
    function onOpen() {
      setDrawerOpen(true);
    }
    window.addEventListener('apolo:openMobileDrawer', onOpen);
    return () => window.removeEventListener('apolo:openMobileDrawer', onOpen);
  }, []);

  async function handleSubmit({ text, model, mode, attachments, directory, github_repo, client_intent_id, environment_mode, permissions_mode }) {
    if (!text) return;
    const store = useAgentStore.getState();
    const intent = buildNewChatIntent({ text, model, mode: environment_mode || 'direct', permissions_mode: permissions_mode || 'ask', kind: 'chat', attachments, directory, github_repo, client_intent_id });
    const initialText = initialCollaborationText(mode, serializeAttachmentsWithContent(text, attachments));
    const res = await store.createCodeSession({
      name: text.length > 60 ? `${text.slice(0, 57)}…` : text,
      description: text,
      scope: 'local',
      ...intentToSessionInput(intent),
      ...(client_intent_id ? { initial_message: { content: initialText, draft_scope: `intent:${client_intent_id}`, attachment_count: attachments?.length || 0 }, flags: { collaboration_mode: mode || 'chat', ...(['ask', 'research'].includes(mode) ? { read_only: true } : {}) } } : {}),
      // Contexto do composer: pasta local vira o directory; repo GitHub é
      // resolvido/clonado num checkout local dentro do createCodeSession.
      ...(directory ? { directory } : {}),
      ...(github_repo ? { github_repo } : {}),
    });
    if (res.inactiveProfile) return res;
    if (res.ok && res.session) {
      // SessionWorkspace auto-envia a 1ª mensagem ao montar (e a sessão já foi
      // ativada dentro de createCodeSession, abrindo a conversa). Plan/Deep
      // research envolvem o texto; o nome da sessão fica o texto original.
      // E1-T02 — anexo com CONTEÚDO (não só metadata): serializeAttachmentsWithContent
      // inline os arquivos texto no prompt (antes a 1ª msg descartava o anexo inteiro).
      store.setPendingFirstMessage(res.session.id, initialText, attachments, Boolean(client_intent_id));
      // #11/#12 — persiste o modo pra o gate readOnly ('ask') valer em TODO turno,
      // não só na 1ª mensagem (antes o prefixo era one-shot e evaporava).
      if (mode) store.setChatMode(res.session.id, mode);
    } else {
      window.dispatchEvent(
        new CustomEvent('apolo:toast', {
          detail: { kind: 'error', message: `Falha ao criar sessão: ${res.error || 'erro desconhecido'}` },
        }),
      );
    }
    return res;
  }

  const goHome = () => {
    setActiveSession(null);
    setChatView('home'); // volta pro composer mesmo se estava em Agenda/Kanban
    // Feedback ao "Nova sessão" mesmo já estando na home: limpa + foca o composer.
    window.dispatchEvent(new CustomEvent('apolo:chatNewSession'));
  };

  const sidebar = <ChatWorkspaceSidebar onNewSession={goHome} />;

  return (
    <div
      ref={shellRef}
      className="cx-shell flex h-full w-full flex-1"
      id="tabpanel-chat"
      role="tabpanel"
      aria-labelledby="tab-chat"
    >
      <div className="hidden lg:flex">{sidebar}</div>
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Sessões">
        {sidebar}
      </MobileDrawer>

      {activeSession ? (
        <div className="flex min-w-0 flex-1">
          <SessionWorkspace
            session={activeSession}
            onBack={goHome}
            onOpenSidebar={() => setDrawerOpen(true)}
            hideGit
          />
          {/* C3 (Aba-Apolo) — rail de tasks sempre-visível em telas largas; lê o
              plano real do planStateStore (alimentado pelo próprio workspace). */}
          <ChatTasksRail sessionId={activeSession.id} />
        </div>
      ) : chatView === 'schedules' ? (
        <main className="flex w-full max-w-full flex-1 flex-col gap-5 overflow-x-clip overflow-y-auto px-3 py-4 sm:gap-6 sm:px-6 sm:py-7">
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight text-apolo-textPrimary">Agenda</h1>
            <p className="text-sm text-apolo-textTertiary">O que roda hoje e nos próximos dias.</p>
          </header>
          <Suspense fallback={<div className="p-6 text-sm text-apolo-textTertiary">Carregando…</div>}>
            <SchedulesView />
          </Suspense>
        </main>
      ) : chatView === 'sprints' ? (
        <main className="flex w-full max-w-full flex-1 flex-col gap-4 overflow-hidden px-3 py-4 sm:px-6 sm:py-6">
          <header className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-apolo-textPrimary">Kanban</h1>
            <span className="text-xs text-apolo-textTertiary">gerenciamento de demandas</span>
          </header>
          <div className="min-h-0 flex-1">
            <Suspense fallback={<div className="p-6 text-sm text-apolo-textTertiary">Carregando…</div>}>
              <KanbanBoard />
            </Suspense>
          </div>
        </main>
      ) : (
        <main className="flex w-full max-w-full flex-1 flex-col items-center justify-center gap-6 overflow-y-auto px-4 py-10">
          <h1 className="cx-greeting flex items-center justify-center gap-3 text-[26px] text-apolo-textPrimary">
            <ApoloEmblem className="h-8 w-8 shrink-0" aria-hidden="true" />
            <span>{greetingFor(user?.name)}</span>
          </h1>
          <ChatHomeComposer key={draftIdentity} onSubmit={handleSubmit} />
        </main>
      )}
    </div>
  );
}
